import React, { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { STATUS } from "./FeriePage";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Laptop, Plus, X, CalendarIcon, Hourglass } from "lucide-react";

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const SmartWorkingPage = () => {
  const [list, setList] = useState([]);
  const [dates, setDates] = useState([]);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sortedDates = useMemo(() => dates.slice().sort((a, b) => a - b), [dates]);

  const load = () => api.get("/smartworking/mine").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!dates.length) { toast.error("Seleziona almeno una giornata"); return; }
    setSubmitting(true);
    try {
      await api.post("/smartworking", { dates: dates.map((d) => format(d, "yyyy-MM-dd")), note });
      toast.success("Richiesta di smart working inviata!");
      setDates([]); setNote(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nell'invio"); }
    finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    try { await api.put(`/smartworking/${id}/cancel`); toast.success("Richiesta annullata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="smartworking-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Laptop size={24} /> Smart Working</h1>
        <p className="text-[14px] text-gray-500 mb-6">Richiedi le giornate di lavoro da remoto. Approvazione da parte del responsabile.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova richiesta</h2>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Giornate da remoto</label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="sw-dates-trigger" className="w-full flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] text-left hover:border-[#16345c] transition-colors">
                        <CalendarIcon size={16} className="text-gray-400" />
                        {dates.length ? `${dates.length} giornate selezionate` : <span className="text-gray-400">Seleziona una o pi\u00f9 date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d || [])} locale={it} />
                    </PopoverContent>
                  </Popover>
                  {dates.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {sortedDates.map((d) => (
                        <span key={d.toISOString()} className="text-[11px] bg-[#16345c1a] text-[#16345c] font-semibold px-2 py-0.5 rounded-full">{format(d, "dd/MM")}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Note (facoltative)</label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="sw-note" placeholder="Attivit\u00e0 previste da remoto..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                </div>
                <button type="submit" disabled={submitting} data-testid="sw-submit" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Invio..." : "Invia richiesta"}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">Le mie richieste</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <Laptop size={40} className="mx-auto mb-3 opacity-40" /> Nessuna richiesta di smart working
              </div>
            ) : (
              <div className="space-y-3" data-testid="sw-list">
                {list.map((sw) => (
                  <div key={sw.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[14px] font-bold text-[#16345c]">{sw.days_count} {sw.days_count === 1 ? "giornata" : "giornate"}</span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[sw.status]?.cls}`}>{STATUS[sw.status]?.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {sw.dates.map((d) => <span key={d} className="text-[12px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{fmt(d)}</span>)}
                      </div>
                      {sw.note && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{sw.note}"</div>}
                      {sw.decision_note && <div className="text-[12px] text-gray-500 mt-1"><b>{sw.approver_name}:</b> {sw.decision_note}</div>}
                    </div>
                    {sw.status === "in_attesa" && (
                      <button onClick={() => cancel(sw.id)} data-testid={`sw-cancel-${sw.id}`} className="shrink-0 flex items-center gap-1 text-[12px] font-semibold text-[#c00510] hover:bg-red-50 px-2 py-1 rounded-sm transition-colors"><X size={14} /> Annulla</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SmartWorkingPage;
